import { getAuthHeaders, API_URL } from './config';
import { Court } from './courts';

export interface Reservation {
    id: string;
    club_id: string;
    court_id: string;
    user_id?: string;
    start_time: string; // ISO
    end_time: string; // ISO
    total_price: number;
    status: 'confirmed' | 'cancelled';
    payment_status: 'paid' | 'unpaid' | 'deposit' | 'refunded' | 'on_spot' | 'succeeded' | 'pending' | string;
    num_players?: number; // Added
    is_open_match?: boolean; // Added
    user_first_name?: string; // Joined
    user_last_name?: string; // Joined
    user_display_name?: string; // Joined
    user_email?: string; // Joined
    match_gender?: 'mixed' | 'men_only' | 'women_only'; // Joined
    match_level_min?: number; // Joined
    match_level_max?: number; // Joined
    title?: string; // For display, usually derived from user or type
    tournament_id?: string; // Joined
    tournament_round?: string; // Joined
    lesson_id?: string; // Joined
    lesson_title?: string; // Joined
    lesson_type?: string; // Joined
    lesson_participants?: { id: string, name: string, avatar?: string }[]; // Joined
    participants?: { user_id: string, team: number }[]; // Added
    match_players_names?: string[]; // Joined (parsed JSON)
    match_players_details?: { name: string, team: number }[]; // Joined (parsed JSON)
    court_name?: string; // Joined
    match_id?: number; // Joined
}

export interface PlanningData {
    date: string;
    courts: Court[];
    reservations: Reservation[];
    pricing_rules?: any[];
}

export const planningService = {
    getConflicts: async (clubId: string, start: string, end: string) => {
        const response = await fetch(`${API_URL}/planning/conflicts`, {
            method: 'POST',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ clubId, start_time: start, end_time: end })
        });
        if (!response.ok) throw new Error("Failed to check conflicts");
        return response.json();
    },
    getAvailability: async (clubId: string, date: string): Promise<PlanningData> => {
        const response = await fetch(`${API_URL}/planning/availability?clubId=${clubId}&date=${date}`, {
            headers: getAuthHeaders()
        });
        if (!response.ok) throw new Error('Failed to fetch planning');
        const json = await response.json();
        return json; // Backend returns { date, courts, reservations ... } directly logic? No, usually { success: true, data: ... } or just data depending on my backend helper.
        // My backend `success` helper typically returns the object directly if I recall correctly, or wraps it? 
        // Looking at `PlanningController.ts`, I used `success(...)`. 
        // Let's verify `utils/response.ts` or similar if I could.
        // But based on previous CourtService fix, `success` returns logic might vary. 
        // Actually, in `CourtController`, I used `success(courts)`. 
        // In frontend `courts.ts`, I had to remove `.data`. So here `json` is likely the data itself.
    },

    getPublicAvailability: async (clubId: string, date: string): Promise<PlanningData> => {
        const response = await fetch(`${API_URL}/planning/availability?clubId=${clubId}&date=${date}`);
        if (!response.ok) throw new Error('Failed to fetch planning');
        const json = await response.json();
        return json;
    },

    createReservation: async (data: Partial<Reservation>): Promise<Reservation> => {
        const response = await fetch(`${API_URL}/planning/reservations`, {
            method: 'POST',
            headers: {
                ...getAuthHeaders(),
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });
        if (!response.ok) {
            const err = await response.json();
            throw new Error(err.error || 'Failed to create reservation');
        }
        return response.json();
    },

    deleteReservation: async (id: string): Promise<void> => {
        const response = await fetch(`${API_URL}/planning/reservations/${id}`, {
            method: 'DELETE',
            headers: getAuthHeaders()
        });
        if (!response.ok) throw new Error('Failed to delete reservation');
    },

    getMonthlyStats: async (clubId: string, year: number, month: number): Promise<Record<string, { totalReservations: number, occupancyRate: number, capacityHours: number }>> => {
        const response = await fetch(`${API_URL}/planning/stats?clubId=${clubId}&year=${year}&month=${month}`, {
            headers: getAuthHeaders()
        });
        if (!response.ok) throw new Error('Failed to fetch stats');
        return response.json();
    }
};
